/**
 * ホームの部品（2026-09-28 creator/home-redesign）。見出し・柱・段・AI エージェントをつなぐ 1 行を小さく分け、
 * A / B（homeFlow.ts HOME_VARIANT）のどちらでも組み立てられるようにしてある。組み立ては HomeStart.tsx、B の入力欄は HomeAsk.tsx。
 * 文言は i18n/en.home.ts・ja.home.ts（見出しの言葉は 1 キーずつ差し替えられる）
 */
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ResendVerificationButton } from '../components/EmailUnverified';
import { INVITE_CODE_PATH } from '../components/InviteNotAppliedNotice';
import type { ProjectSummary } from '../api';
import { ago, CopyButton } from '../components/ui';
import { mcpEndpoint, type Step, type StepId } from '../homeFlow';
import { CLI } from '../cliCommand';
import { CONNECT_PATH } from '../connectPage';
import { useResource } from '../hooks';
import { useT } from '../i18n';
import { reportOnboardingStep } from '../onboardingReport';
import { isLaunchHidden } from '../launchHidden';
import { money } from '../money';
import { useSession } from '../session';
import { PREVIEW_NAME, PRODUCT_NAME } from '../productName';
import { connectPromptFor, pickConnectPrompt, previewConnectPromptFor, startPageUrl } from '../webConnect';
import { cameFromAd } from '../adVisit';
import { deviceStorage } from '../lastLogin';

// ---------------------------------------------------------------- 3 つの柱

const PILLARS = ['publish', 'data', 'money'] as const;

export function Pillars() {
  const { t } = useT();
  return (
    <ul className="home-pillars" aria-label={t('home.pillarsLabel')}>
      {PILLARS.map((p) => (
        <li key={p} className="home-pillar">
          <span className="home-pillar-icon" aria-hidden="true">
            <PillarIcon kind={p} />
          </span>
          <span className="home-pillar-title">{t(`home.pillar.${p}.title`)}</span>
          <span className="home-pillar-ready">{t('home.ready')}</span>
          <span className="home-pillar-body">{t(p === 'money' && isLaunchHidden('payouts') ? 'home.pillar.money.bodyLaunch' : `home.pillar.${p}.body`)}</span>
        </li>
      ))}
    </ul>
  );
}

function PillarIcon({ kind }: { kind: (typeof PILLARS)[number] }) {
  // 線だけの小さな絵（色は currentColor = テーマに合わせて変わる）。絵文字は使わない（環境で見た目が変わる）
  return (
    <svg viewBox="0 0 24 24" width="22" height="22">
      {kind === 'publish' && (
        <>
          <circle cx="12" cy="12" r="9" />
          <path d="M3 12h18M12 3c2.8 3 2.8 15 0 18M12 3c-2.8 3-2.8 15 0 18" />
        </>
      )}
      {kind === 'data' && (
        <>
          <ellipse cx="12" cy="6" rx="7" ry="3" />
          <path d="M5 6v12c0 1.7 3.1 3 7 3s7-1.3 7-3V6M5 12c0 1.7 3.1 3 7 3s7-1.3 7-3" />
        </>
      )}
      {kind === 'money' && (
        <>
          <circle cx="12" cy="12" r="9" />
          <path d="M15 9.2c-.6-.8-1.7-1.2-3-1.2-1.7 0-3 .8-3 2s1.3 1.7 3 2 3 .8 3 2-1.3 2-3 2c-1.3 0-2.4-.4-3-1.2M12 6.5v11" />
        </>
      )}
    </svg>
  );
}

// ---------------------------------------------------------------- 次の一歩の段

/**
 * メールの確認の段の文。招待クレジットが確認待ち（homeFlow.ts inviteVerifyAmount が額を返す）なら「確認すると {amount} が入ります」の文
 * （2026-09-29 creator/launch-close-2・Money の前払いの道 17: 確認待ちの人に「入金」ではなく「確認すれば入る」を言う）。A・B・C で共通
 */
export function useVerifyBody(invite: { granted: number; currency: string } | null | undefined): string {
  const { t } = useT();
  const { developer } = useSession();
  const email = developer?.email ?? '';
  return invite ? t('home.step.verify.bodyInvite', { email, amount: money(invite.granted, invite.currency) }) : t('home.step.verify.body', { email });
}

export function StepItem({ step, n, mcpUrl, invite }: { step: Step; n?: number; mcpUrl: string | null; invite?: { granted: number; currency: string } | null }) {
  const { t } = useT();
  const title = t(`home.step.${step.id}.title`);
  if (step.state === 'done') {
    return (
      <li className="home-step is-done">
        <span className="home-step-mark" aria-hidden="true">
          <svg viewBox="0 0 16 16" width="12" height="12">
            <path d="M3 8.5l3 3 7-7" />
          </svg>
        </span>
        <span className="home-step-title">{title}</span>
        <span className="home-step-state">{t('home.stepDone')}</span>
        {/* A の「つないだ（済み）」の段にも /connect への道（HomeC と同じ・2026-10-07 console/connect-again） */}
        {step.id === 'connect' && (
          <Link to={CONNECT_PATH} className="home-connect-again">
            {t('connect.elsewhereLink')}
          </Link>
        )}
      </li>
    );
  }
  if (step.state === 'todo') {
    return (
      <li className="home-step is-todo">
        <span className="home-step-mark" aria-hidden="true">
          {n}
        </span>
        <span className="home-step-title">{title}</span>
      </li>
    );
  }
  return (
    <li className="home-step is-current" aria-current="step">
      <span className="home-step-mark" aria-hidden="true">
        {n ?? '→'}
      </span>
      <div className="home-step-main">
        <h3 className="home-step-title">{title}</h3>
        <StepBody id={step.id} mcpUrl={mcpUrl} invite={invite} />
      </div>
    </li>
  );
}

function StepBody({ id, mcpUrl, invite }: { id: StepId; mcpUrl: string | null; invite?: { granted: number; currency: string } | null }) {
  const { t } = useT();
  const verifyBody = useVerifyBody(invite);
  switch (id) {
    case 'verify':
      return (
        <>
          <p>{verifyBody}</p>
          <div className="home-step-actions">
            <ResendVerificationButton className="btn btn-primary" />
          </div>
        </>
      );
    case 'connect':
      return <ConnectBody mcpUrl={mcpUrl} />;
    case 'topUp':
      return (
        <>
          <p>{t('home.step.topUp.body')}</p>
          <div className="home-step-actions">
            <Link to="/money/prepaid" className="btn btn-primary">
              {t('home.step.topUp.cta')}
            </Link>
            {/* 招待コードのある人の道（2026-10-29 core/open-signup-build・招待なしで登録した人は $0 から）: 欄は前払い残高の画面の下 = 直に飛ぶ。
                文字のリンク（2026-10-29 core/open-signup-polish・Rauno 役: ボタンは「入金する」1 つ） */}
            <Link to={INVITE_CODE_PATH} className="home-step-link">
              {t('invite.haveCode')}
            </Link>
          </div>
        </>
      );
    case 'talk':
      return (
        <>
          <p>{t('home.step.talk.body')}</p>
          <ul className="home-examples">
            {(['home.example.1', 'home.example.2', 'home.example.3', 'home.example.4'] as const).map((k) => (
              <li key={k}>
                <q>{t(k)}</q>
                <CopyButton text={t(k)} />
              </li>
            ))}
          </ul>
        </>
      );
    case 'publish':
      return <p>{t('home.step.publish.body')}</p>;
  }
}

// ---------------------------------------------------------------- AI をつなぐ（A の段・B の小さな 1 行・C・/guide で使い回す）

/**
 * 「つなぐ」の段の中身（ホームの段・B の小さな 1 行・/guide の始め方 1 で共通）。2026-10-11 console/one-connect で 1 つの文だけにした:
 *   どの AI にも貼る文（webConnect.ts connectPromptFor）＋コピー ＋ 補足 1 行（どれに貼っても使える）。
 * WHY 1 つか: founder「ひとまとめでいいんじゃないの？ あとはエージェントがいい感じでやってくれるでしょ。人間はコピペするだけでいい」。
 *   以前は「Where do you use AI?」の 2 つのボタン（パソコンの AI エージェント / Web・スマホの Claude・ChatGPT）で選ばせ、選んだ方の文を出していた
 *   （その前はエージェント向けの頼み文＋エージェントごとの 1 行のタブ）。選ぶ・どれをコピーするか迷う手間を無くし、AI に自分の居場所を判断させる
 *   （判断の仕方はサイトの案内のページ /docs/connect/ = AI が読む）。選んだ方を覚えていた localStorage（kumo.console.connectWay・agentTab）は使わなくなった。
 * 文が作れない環境（サイトの URL も MCP の URL も無い）では、推測で URL を作らず、ドキュメントの場所の名前だけを出す。
 * compact = B の小さな 1 行（ボタンを主なボタンにしない）
 */
export function ConnectBody({ mcpUrl, compact = false }: { mcpUrl: string | null; compact?: boolean }) {
  const { t, locale } = useT();
  // この Kumo の API の公開 URL（connect --api に入れる）と Preview の MCP の URL（previewMcpUrl・開いた形のときだけサーバーが返す）
  const platform = useResource<{ apiUrl?: unknown; previewMcpUrl?: unknown }>('/v1/platform/info');
  // Preview が開いているときは、貼る文を Preview の 1 つだけにする（2026-10-16 founder の決定「1 か所に本番とプレビューの両方があるのが間違い」）。
  //   経緯: console/guide-preview（同日）は /guide だけが previewMcpUrl を渡していた → console/home-preview でホーム（A の段・B の小さな 1 行・C）も
  //   同じ文にそろえるため、呼ぶ側に渡させず ConnectBody 自身が読む（ConnectBody を使う所 = 貼る文を出す所すべてが同じ決まりになる・渡し忘れが無い）。
  //   null（閉じている・URL が怪しい）ならいつもの文のまま。決め方は webConnect.ts pickConnectPrompt（テストは webConnect.test.ts）
  // 広告から来た人（adVisit.ts の印・2026-10-18 gtm/early-fixes）: 締めの頼みを「このゲームを公開して」に（connectPrompt.mjs askLine）。ほかの人は今までの文
  const ownGame = cameFromAd(deviceStorage(), Date.now());
  const prompt = pickConnectPrompt({
    platformLoading: platform.loading && !platform.data,
    preview: () =>
      previewConnectPromptFor({
        mcpUrl: mcpEndpoint(platform.data?.previewMcpUrl),
        // 手順のページへのリンク（2026-10-17 console/ai-does-connect・webConnect.ts previewConnectPromptFor）
        siteUrl: import.meta.env.VITE_KUMO_SITE_URL,
        productName: PRODUCT_NAME,
        previewName: PREVIEW_NAME,
        locale,
        ownGame
      }),
    regular: () =>
      connectPromptFor({
        siteUrl: import.meta.env.VITE_KUMO_SITE_URL,
        mcpUrl,
        apiUrl: platform.data?.apiUrl,
        cli: CLI,
        productName: PRODUCT_NAME,
        locale,
        ownGame
      })
  });
  // オンボーディングの段（onboardingSteps.ts・2026-10-18 ops/gaps-activity）: つなぐ文が画面に出た = connect_guide_opened（1 画面の読み込みにつき 1 回）。
  //   ホームの「つなぐ」の段も使い方ガイドもこの部品 = どちらで見ても同じ段。早い return より前に置く（フックの順を変えない）
  const shown = prompt !== 'loading' && !!prompt;
  const startUrl = startPageUrl(import.meta.env.VITE_KUMO_SITE_URL, locale);
  useEffect(() => {
    if (shown) reportOnboardingStep('connect_guide_opened');
  }, [shown]);
  // 読み込み中は何も出さない（いつもの文を一瞬出すと、それをコピーした人がいつもの方につないでしまう）
  if (prompt === 'loading') return null;
  if (!prompt) return <p className="home-connect-note">{t('home.step.connect.noPrompt')}</p>;
  return (
    <div className="home-connect">
      <p className="home-connect-label">{t('home.step.connect.copyLabel')}</p>
      {/* ホームと使い方ガイド（compact でない）: コピーは枠の下の主のボタン（2026-10-18 design/rauno-dashboard2・rauno-audit.md ホームの行）。
          WHY: この段でたった 1 つの大事な操作なのに、以前は枠の右上の小さな札だった。スマホは幅いっぱい（styles.css .home-connect-copy）。
          どちらの画面もほかに主のボタンは無い（1 画面に 1 つ）。compact（ホームの B の小さな 1 行・HomeStart.tsx）は前のまま枠の隅の札 */}
      <div className={`home-command home-ask-block is-multiline ${compact ? 'home-command-sm' : ''}`}>
        <pre className="home-web-prompt" tabIndex={0}>
          {prompt}
        </pre>
        {compact && <CopyButton text={prompt} onCopy={() => reportOnboardingStep('connect_command_copied')} />}
      </div>
      {!compact && <CopyButton text={prompt} variant="primary" className="home-connect-copy" onCopy={() => reportOnboardingStep('connect_command_copied')} />}
      <p className="home-connect-note">{t('home.step.connect.anyAi')}</p>
      {/* 初めての人に渡す「はじめる」のページ（2026-10-25 site/start-page・webConnect.ts startPageUrl）。compact（ホームの B の小さな 1 行）には出さない = 1 行のまま */}
      {!compact && startUrl && (
        <p className="home-connect-note">
          <a href={startUrl} target="_blank" rel="noopener">
            {t('home.step.connect.startPage')} <span aria-hidden="true">→</span>
          </a>
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- 見出し（A）と柱の 1 行（B）

/** A の見出し: 言い切り（home.title）＋リード（home.lead・ゲームがある人には出さない）。文言は en.home.ts / ja.home.ts の 1 キーずつ */
export function HeroStatement({ lead }: { lead: boolean }) {
  const { t } = useT();
  return (
    <header className="home-hero">
      <p className="eyebrow">{t('home.eyebrow')}</p>
      <h1 className="home-title">{t('home.title')}</h1>
      {lead && <p className="home-lead">{t('home.lead')}</p>}
    </header>
  );
}

/** B の柱: 見出しの下の小さな 1 行（home.b.readyLine）。3 つの柱の名前を点で並べるだけ・リンクにしない */
export function PillarsLine() {
  const { t } = useT();
  return (
    <p className="home-ready-line">
      <span className="home-ready-dot" aria-hidden="true" />
      {t('home.b.readyLine')}
    </p>
  );
}

// ---------------------------------------------------------------- 準備の段（B: 入力欄の下に控えめに）

/**
 * B のメール確認・入金。A の段と同じ文言を使い、ボタンは小さい ghost にする
 * （B の主な行動は入力欄とコピー = ここを主なボタンにしない。順番の考えは homeFlow.ts setupNotices）
 */
export function SetupNotice({ id, invite }: { id: 'verify' | 'topUp'; invite?: { granted: number; currency: string } | null }) {
  const { t } = useT();
  const verifyBody = useVerifyBody(invite);
  return (
    <li className="home-setup">
      <div className="home-setup-text">
        <strong>{t(`home.step.${id}.title`)}</strong>
        <span>{id === 'verify' ? verifyBody : t('home.step.topUp.body')}</span>
      </div>
      {id === 'verify' ? (
        <ResendVerificationButton className="btn btn-ghost btn-sm" />
      ) : (
        <span className="home-step-actions">
          <Link to="/money/prepaid" className="btn btn-ghost btn-sm">
            {t('home.step.topUp.cta')}
          </Link>
          {/* 招待コードのある人の道（2026-10-29 core/open-signup-build・上の段と同じ = 文字のリンク） */}
          <Link to={INVITE_CODE_PATH} className="home-step-link">
            {t('invite.haveCode')}
          </Link>
        </span>
      )}
    </li>
  );
}

// ---------------------------------------------------------------- 自分のプロジェクトの一覧（A・B・C 共通）

export function ProjectGrid({ projects }: { projects: ProjectSummary[] }) {
  const { t } = useT();
  return (
    <ul className="project-grid">
      {projects.map((p, i) => (
        <li key={p.id} style={{ ['--i' as string]: i }}>
          <Link to={`/p/${p.id}/development/overview`} className="project-card">
            <span className="project-initial" aria-hidden="true">
              {p.name.slice(0, 1).toUpperCase()}
            </span>
            <span className="project-name">
              {p.name}
              {/* 共同作業者として手伝っているプロジェクト（2026-10-22 core/handoff-1b）= 自分の物と見分ける */}
              {p.role === 'member' && <span className="member-badge">{t('members.badge')}</span>}
            </span>
            <span className="project-slug">{p.slug}</span>
            <span className="project-date muted">{t('projects.created', { ago: ago(p.createdAt) })}</span>
          </Link>
        </li>
      ))}
    </ul>
  );
}
